import { getDebugState } from '@store/debugger';

import { useText } from './use-text';
import {
    PluralityTranslationOption,
    ReplaceTranslationOption,
    TranslateOptions,
} from './use-translate.types';

let dictionaryPromise: Promise<Record<string, string>> | null = null;
let DICTIONARY: Record<string, string>;

const showDictionaryKeys = getDebugState().showDictionaryKeys;
const normalizeKey = (keyRaw: string) => keyRaw.toLowerCase().replace(/[^a-zA-Z0-9A-zÀ-ú]/g, '');

const applyReplace = (translation: string, { id, value }: ReplaceTranslationOption) => {
    if (!translation.match(new RegExp(`\\[${id}\\]`))) return translation;
    return translation.replace(new RegExp(`\\[${id}\\]`, 'g'), value);
};

const applyPlurality = (translation: string, { id, value, total }: PluralityTranslationOption) => {
    const plurality = translation.match(
        new RegExp(
            `\\{id:\\s*${id}\\s*\\/one:([^\\/]*)\\/many:([^\\/\\}]*)(?:\\/none:([^\\/\\}]*))?(?:\\/max:([^\\}]*))?\\}`,
        ),
    );

    if (!plurality) return translation;
    let output: string;

    if (value <= 0) output = plurality[3] || plurality[2];
    else if (total && value >= total && plurality[4]) output = plurality[4];
    else if (value === 1) output = plurality[1];
    else output = plurality[2];

    return translation.replace(plurality[0], output.trim());
};

const applyTranslateOptions = (translation: string, options?: TranslateOptions) => {
    options?.plurality?.forEach((option) => {
        translation = applyPlurality(translation, option);
    });

    options?.replace?.forEach((option) => {
        translation = applyReplace(translation, option);
    });

    return translation;
};

const getDictionary = async () => {
    if (!!DICTIONARY) return DICTIONARY;

    const storedDictionary = sessionStorage.getItem('dictionary');
    if (storedDictionary && storedDictionary !== 'null') {
        try {
            DICTIONARY = JSON.parse(storedDictionary);
            return DICTIONARY;
        } catch (error) {
            console.error(`Issue with parsing storage dictionary: ${error}`);
        }
    }

    if (!dictionaryPromise) dictionaryPromise = fetchDictionary();

    try {
        return await dictionaryPromise;
    } catch (error) {
        dictionaryPromise = null;
        throw error;
    }
};

const fetchDictionary = async () => {
    const { dictionaryUrl } = window.__ENVIRONMENT_DETAILS__;

    try {
        DICTIONARY = await fetch(dictionaryUrl).then((r) => r.json());
        sessionStorage.setItem('dictionary', JSON.stringify(DICTIONARY));
    } catch (error) {
        throw new Error(`Issue with loading dictionary: ${error}`);
    }

    return DICTIONARY;
};

export const translateItem = async (dictionaryKey: string, options?: TranslateOptions) => {
    const dictionary = await getDictionary();
    const translation = applyTranslateOptions(dictionary[dictionaryKey] ?? '', options);

    return showDictionaryKeys ? `${translation} :: ${dictionaryKey}` : translation;
};

export const translateItems = async <T>(
    keys: { [K in keyof T]?: string },
    options?: TranslateOptions,
): Promise<{ [K in keyof T]?: string }> => {
    const translated: { [K in keyof T]?: string } = {};
    for (const key of Object.keys(keys) as (keyof T)[]) {
        if (!keys[key]) continue;
        translated[key] = await translateItem(keys[key]!, options);
    }
    return translated;
};

/**
 * @description Translates a single characteristic value. Format is as following: 'Feature.category.value'.
 * The `value` is based on the value in SF. Without white spaces and special characters. Numbers + accented characters are kept.
 * "Value example 1" will be: "valueExample1"
 * @param {string} prefix
 * @param {string} value
 * @return {*}
 */
export const normalizedTranslateItem = async (prefix: string, value: string) => {
    const key = normalizeKey(value);
    const translation = await translateItem(`${prefix}${key}`);
    return translation.length ? translation : value;
};

export const useTranslate = (item: string, options?: TranslateOptions) => {
    const [text, setText] = useText('');
    translateItem(item).then((result) => {
        if (!result) return;

        result = applyTranslateOptions(result, options);

        if (options?.html) {
            // The placeholder is already in the DOM because translateItem always resolves asynchronously.
            const template = document.createElement('template');
            template.innerHTML = result;
            text.replaceWith(template.content);
        } else {
            setText(result);
        }

        if (options?.onUpdate) {
            options.onUpdate();
        }
    });
    return text;
};
